import React, { useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useUserOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  BookOpen,
  Shield,
  Users,
  Clock,
  Download,
  Star,
  Search,
  FileText,
  Video,
  ExternalLink,
  Bookmark,
  Share2,
  Play,
  Award,
  Lightbulb,
  CheckCircle,
  AlertTriangle,
  Info
} from "lucide-react";

interface Resource {
  id: string;
  title: string;
  description: string;
  type: 'template' | 'guide' | 'video' | 'article';
  category: 'security' | 'parental' | 'compliance' | 'best-practices';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration?: string;
  rating: number;
  downloads: number;
  tags: string[];
  url?: string;
  isBookmarked?: boolean;
}

const policyTemplates: Resource[] = [
  {
    id: '1',
    title: 'Enterprise Security Policy',
    description: 'Comprehensive security policy for large organizations with advanced threat protection',
    type: 'template',
    category: 'security',
    difficulty: 'advanced',
    duration: '15 min setup',
    rating: 4.8,
    downloads: 1247,
    tags: ['enterprise', 'threat-protection', 'compliance', 'advanced']
  },
  {
    id: '2',
    title: 'Family Protection Suite',
    description: 'Complete parental control policy with age-appropriate content filtering',
    type: 'template',
    category: 'parental',
    difficulty: 'beginner',
    duration: '10 min setup',
    rating: 4.9,
    downloads: 2156,
    tags: ['family', 'parental-controls', 'content-filtering', 'easy']
  },
  {
    id: '3',
    title: 'Healthcare Compliance Policy',
    description: 'HIPAA-compliant DNS policy for healthcare organizations',
    type: 'template',
    category: 'compliance',
    difficulty: 'intermediate',
    duration: '20 min setup',
    rating: 4.7,
    downloads: 892,
    tags: ['healthcare', 'hipaa', 'compliance', 'regulatory']
  },
  {
    id: '4',
    title: 'Educational Institution Policy',
    description: 'Student-focused policy with educational content access and safety measures',
    type: 'template',
    category: 'compliance',
    difficulty: 'intermediate',
    duration: '12 min setup',
    rating: 4.6,
    downloads: 1567,
    tags: ['education', 'student-safety', 'content-access', 'moderate']
  }
];

const guides: Resource[] = [
  {
    id: '5',
    title: 'DNS Security Fundamentals',
    description: 'Learn the basics of DNS security and why it matters for your organization',
    type: 'guide',
    category: 'best-practices',
    difficulty: 'beginner',
    duration: '20 min read',
    rating: 4.5,
    downloads: 3421,
    tags: ['basics', 'fundamentals', 'introduction', 'security-101']
  },
  {
    id: '6',
    title: 'Advanced Threat Detection',
    description: 'Advanced techniques for detecting and blocking sophisticated cyber threats',
    type: 'guide',
    category: 'security',
    difficulty: 'advanced',
    duration: '45 min read',
    rating: 4.8,
    downloads: 1234,
    tags: ['advanced', 'threat-detection', 'cybersecurity', 'expert']
  },
  {
    id: '7',
    title: 'Policy Management Best Practices',
    description: 'How to effectively manage and maintain DNS policies at scale',
    type: 'guide',
    category: 'best-practices',
    difficulty: 'intermediate',
    duration: '30 min read',
    rating: 4.7,
    downloads: 1892,
    tags: ['management', 'best-practices', 'scaling', 'moderate']
  }
];

const videos: Resource[] = [
  {
    id: '8',
    title: 'Getting Started with DNS Filtering',
    description: 'Step-by-step video tutorial for setting up your first DNS policy',
    type: 'video',
    category: 'best-practices',
    difficulty: 'beginner',
    duration: '12 min',
    rating: 4.6,
    downloads: 5678,
    tags: ['tutorial', 'setup', 'first-time', 'video-guide']
  },
  {
    id: '9',
    title: 'Advanced Policy Configuration',
    description: 'Deep dive into advanced policy features and customization options',
    type: 'video',
    category: 'security',
    difficulty: 'advanced',
    duration: '28 min',
    rating: 4.9,
    downloads: 2341,
    tags: ['advanced', 'configuration', 'customization', 'deep-dive']
  }
];

export default function Library() {
  const { data: organization } = useUserOrganization();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [bookmarkedResources, setBookmarkedResources] = useState<string[]>([]);

  if (!organization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Organization Required</h2>
          <p className="text-muted-foreground">
            Please set up your organization to access the resource library.
          </p>
        </div>
      </div>
    );
  }

  const allResources = [...policyTemplates, ...guides, ...videos];

  const filteredResources = allResources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'all' || resource.difficulty === selectedDifficulty;

    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const toggleBookmark = (resourceId: string) => {
    setBookmarkedResources(prev =>
      prev.includes(resourceId)
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'security': return <Shield className="h-4 w-4" />;
      case 'parental': return <Users className="h-4 w-4" />;
      case 'compliance': return <CheckCircle className="h-4 w-4" />;
      case 'best-practices': return <Lightbulb className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'template': return <FileText className="h-4 w-4" />;
      case 'guide': return <BookOpen className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      case 'article': return <FileText className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SiteHeader />
      <SidebarInset>
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Resource Library</h1>
              <p className="text-muted-foreground">
                Policy templates, guides, and best practices for {organization.name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Bookmark className="h-4 w-4 mr-2" />
                Bookmarks ({bookmarkedResources.length})
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                Share Library
              </Button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex gap-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm bg-background"
              >
                <option value="all">All Categories</option>
                <option value="security">Security</option>
                <option value="parental">Parental Controls</option>
                <option value="compliance">Compliance</option>
                <option value="best-practices">Best Practices</option>
              </select>

              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm bg-background"
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          {/* Featured Resources */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              Featured Resources
            </h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {allResources
                .filter(r => r.rating >= 4.7)
                .slice(0, 3)
                .map((resource) => (
                <Card key={resource.id} className="relative group hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(resource.type)}
                        <Badge variant="outline" className={getDifficultyColor(resource.difficulty)}>
                          {resource.difficulty}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleBookmark(resource.id)}
                        className={`opacity-0 group-hover:opacity-100 transition-opacity ${
                          bookmarkedResources.includes(resource.id) ? 'text-yellow-500' : ''
                        }`}
                      >
                        <Bookmark className="h-4 w-4" />
                      </Button>
                    </div>
                    <CardTitle className="text-lg">{resource.title}</CardTitle>
                    <CardDescription className="line-clamp-2">
                      {resource.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3 w-3 ${
                              i < Math.floor(resource.rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="text-sm text-muted-foreground ml-1">
                          {resource.rating}
                        </span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {resource.downloads.toLocaleString()} downloads
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-1 mb-3">
                      {resource.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {resource.duration}
                      </div>
                      <Button size="sm" className="w-full">
                        {resource.type === 'video' ? (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            Watch
                          </>
                        ) : resource.type === 'template' ? (
                          <>
                            <Download className="h-3 w-3 mr-1" />
                            Use Template
                          </>
                        ) : (
                          <>
                            <BookOpen className="h-3 w-3 mr-1" />
                            Read
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Main Content Tabs */}
          <Tabs defaultValue="templates" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="templates">Templates</TabsTrigger>
              <TabsTrigger value="guides">Guides</TabsTrigger>
              <TabsTrigger value="videos">Videos</TabsTrigger>
              <TabsTrigger value="bookmarks">Bookmarks</TabsTrigger>
            </TabsList>

            {/* Templates Tab */}
            <TabsContent value="templates" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Policy Templates</h2>
                <Badge variant="outline">
                  {filteredResources.filter(r => r.type === 'template').length} templates
                </Badge>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredResources
                  .filter(r => r.type === 'template')
                  .map((resource) => (
                  <Card key={resource.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(resource.category)}
                          <Badge variant="outline" className={getDifficultyColor(resource.difficulty)}>
                            {resource.difficulty}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleBookmark(resource.id)}
                          className={bookmarkedResources.includes(resource.id) ? 'text-yellow-500' : ''}
                        >
                          <Bookmark className="h-4 w-4" />
                        </Button>
                      </div>
                      <CardTitle className="text-lg">{resource.title}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {resource.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3 w-3 ${
                                i < Math.floor(resource.rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'
                              }`}
                            />
                          ))}
                          <span className="text-sm text-muted-foreground ml-1">
                            {resource.rating}
                          </span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {resource.downloads.toLocaleString()}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-1 mb-3">
                        {resource.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {resource.duration}
                        </div>
                        <Button size="sm" className="w-full">
                          <Download className="h-3 w-3 mr-1" />
                          Use Template
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Guides Tab */}
            <TabsContent value="guides" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Guides & Articles</h2>
                <Badge variant="outline">
                  {filteredResources.filter(r => r.type === 'guide').length} guides
                </Badge>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredResources
                  .filter(r => r.type === 'guide')
                  .map((resource) => (
                  <Card key={resource.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(resource.category)}
                          <Badge variant="outline" className={getDifficultyColor(resource.difficulty)}>
                            {resource.difficulty}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleBookmark(resource.id)}
                          className={bookmarkedResources.includes(resource.id) ? 'text-yellow-500' : ''}
                        >
                          <Bookmark className="h-4 w-4" />
                        </Button>
                      </div>
                      <CardTitle className="text-lg">{resource.title}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {resource.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3 w-3 ${
                                i < Math.floor(resource.rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'
                              }`}
                            />
                          ))}
                          <span className="text-sm text-muted-foreground ml-1">
                            {resource.rating}
                          </span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {resource.downloads.toLocaleString()}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-1 mb-3">
                        {resource.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {resource.duration}
                        </div>
                        <Button size="sm" className="w-full">
                          <BookOpen className="h-3 w-3 mr-1" />
                          Read Guide
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Video Tutorials</h2>
                <Badge variant="outline">
                  {filteredResources.filter(r => r.type === 'video').length} videos
                </Badge>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredResources
                  .filter(r => r.type === 'video')
                  .map((resource) => (
                  <Card key={resource.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(resource.category)}
                          <Badge variant="outline" className={getDifficultyColor(resource.difficulty)}>
                            {resource.difficulty}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleBookmark(resource.id)}
                          className={bookmarkedResources.includes(resource.id) ? 'text-yellow-500' : ''}
                        >
                          <Bookmark className="h-4 w-4" />
                        </Button>
                      </div>
                      <CardTitle className="text-lg">{resource.title}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {resource.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3 w-3 ${
                                i < Math.floor(resource.rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'
                              }`}
                            />
                          ))}
                          <span className="text-sm text-muted-foreground ml-1">
                            {resource.rating}
                          </span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {resource.downloads.toLocaleString()}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-1 mb-3">
                        {resource.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {resource.duration}
                        </div>
                        <Button size="sm" className="w-full">
                          <Play className="h-3 w-3 mr-1" />
                          Watch Video
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Bookmarks Tab */}
            <TabsContent value="bookmarks" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Bookmarked Resources</h2>
                <Badge variant="outline">
                  {bookmarkedResources.length} bookmarks
                </Badge>
              </div>

              {bookmarkedResources.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Bookmark className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">No bookmarks yet</h3>
                    <p className="text-muted-foreground">
                      Start bookmarking resources you find useful
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {allResources
                    .filter(r => bookmarkedResources.includes(r.id))
                    .map((resource) => (
                    <Card key={resource.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(resource.type)}
                            <Badge variant="outline" className={getDifficultyColor(resource.difficulty)}>
                              {resource.difficulty}
                            </Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleBookmark(resource.id)}
                            className="text-yellow-500"
                          >
                            <Bookmark className="h-4 w-4 fill-current" />
                          </Button>
                        </div>
                        <CardTitle className="text-lg">{resource.title}</CardTitle>
                        <CardDescription className="line-clamp-2">
                          {resource.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-3 w-3 ${
                                  i < Math.floor(resource.rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="text-sm text-muted-foreground ml-1">
                              {resource.rating}
                            </span>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {resource.downloads.toLocaleString()}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-1 mb-3">
                          {resource.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {resource.duration}
                          </div>
                          <Button size="sm" className="w-full">
                            {resource.type === 'video' ? (
                              <>
                                <Play className="h-3 w-3 mr-1" />
                                Watch
                              </>
                            ) : resource.type === 'template' ? (
                              <>
                                <Download className="h-3 w-3 mr-1" />
                                Use Template
                              </>
                            ) : (
                              <>
                                <BookOpen className="h-3 w-3 mr-1" />
                                Read
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
